import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import type {
  BadgeApplication,
  BadgeApplicationsParams,
  BadgeApplicationsResponse,
  BadgeRule,
  BadgeRulesResponse,
  BadgeStats,
  BadgeType,
  BadgeTypesResponse,
  BadgeVerification,
} from '@/client/types/badges';

const BASE = '/api/v2/admin/badges';

export const badgeKeys = {
  all: ['badges-admin'] as const,
  applications: (params: BadgeApplicationsParams) =>
    [...badgeKeys.all, 'applications', params] as const,
  types: (activeOnly?: boolean) => [...badgeKeys.all, 'types', activeOnly ?? false] as const,
  rules: (btid: number) => [...badgeKeys.all, 'rules', btid] as const,
  stats: () => [...badgeKeys.all, 'stats'] as const,
  asset: (asid: number) => [...badgeKeys.all, 'asset', asid] as const,
  verification: (vid: number) => [...badgeKeys.all, 'verification', vid] as const,
};

/** ff-api wraps payloads as `{ status, data }`. */
const unwrapEnvelope = (raw: unknown): any =>
  raw != null && typeof raw === 'object' && 'status' in (raw as object) && 'data' in (raw as object)
    ? (raw as { data: unknown }).data
    : raw;

const buildSearchParams = (raw: Record<string, unknown>): string => {
  const params = Object.entries(raw).reduce<Record<string, string>>((acc, [k, v]) => {
    if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
    return acc;
  }, {});
  const qs = new URLSearchParams(params).toString();
  return qs ? `?${qs}` : '';
};

const normalizeApplications = (raw: unknown): BadgeApplicationsResponse => {
  const payload = unwrapEnvelope(raw);
  const applications: BadgeApplication[] = Array.isArray(payload?.applications)
    ? payload.applications
    : Array.isArray(payload)
      ? payload
      : [];
  return {
    applications,
    total: Number(payload?.total ?? applications.length) || 0,
    page: Number(payload?.page ?? 1) || 1,
    page_size: Number(payload?.page_size ?? applications.length) || 20,
  };
};

const normalizeTypes = (raw: unknown): BadgeType[] => {
  const payload = unwrapEnvelope(raw) as BadgeTypesResponse | BadgeType[];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.types)) return payload.types;
  if (Array.isArray(payload?.badge_types)) return payload.badge_types!;
  return [];
};

const normalizeRules = (raw: unknown): BadgeRule[] => {
  const payload = unwrapEnvelope(raw) as BadgeRulesResponse | BadgeRule[];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rules)) return payload.rules;
  return [];
};

const normalizeStats = (raw: unknown): BadgeStats => {
  const payload = unwrapEnvelope(raw);
  if (payload != null && typeof payload === 'object') return payload as BadgeStats;
  return {};
};

const normalizeVerification = (raw: unknown): BadgeVerification | null => {
  const payload = unwrapEnvelope(raw);
  if (payload == null) return null;
  const obj = (payload as any)?.verification ?? payload;
  if (obj == null || typeof obj !== 'object') return null;
  const checklist =
    (Array.isArray(obj.checklist) && obj.checklist) ||
    (Array.isArray(obj.items) && obj.items) ||
    [];
  return { ...obj, checklist } as BadgeVerification;
};

const normalizeAsset = (raw: unknown): Asset | null => {
  const payload = unwrapEnvelope(raw);
  if (payload == null) return null;
  const obj = (payload as any)?.asset ?? payload;
  if (obj == null || typeof obj !== 'object') return null;
  return obj as Asset;
};

// ─── Applications ─────────────────────────────────────────────────────────────

const useBadgeApplicationsFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (params: BadgeApplicationsParams = {}) => {
    const qs = buildSearchParams(params as Record<string, unknown>);
    const raw = await request<unknown>(`${BASE}/applications${qs}`);
    return normalizeApplications(raw);
  }, 'Failed to fetch badge applications');
};

export const useBadgeApplicationsQuery = (
  params: BadgeApplicationsParams = {},
  enabled = true,
) => {
  const queryFn = useBadgeApplicationsFn();
  return useQuery({
    queryKey: badgeKeys.applications(params),
    queryFn: () => queryFn(params),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

// ─── Types ────────────────────────────────────────────────────────────────────

const useBadgeTypesFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (activeOnly = false) => {
    const qs = activeOnly ? '?active_only=true' : '';
    const raw = await request<unknown>(`${BASE}/types${qs}`);
    return normalizeTypes(raw);
  }, 'Failed to fetch badge types');
};

export const useBadgeTypesQuery = (activeOnly = false, enabled = true) => {
  const queryFn = useBadgeTypesFn();
  return useQuery({
    queryKey: badgeKeys.types(activeOnly),
    queryFn: () => queryFn(activeOnly),
    enabled,
    staleTime: DEFAULT_STALE_TIME * 6,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Rules ────────────────────────────────────────────────────────────────────

const useBadgeRulesFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (btid: number) => {
    const raw = await request<unknown>(`${BASE}/types/${btid}/rules`);
    return normalizeRules(raw);
  }, 'Failed to fetch badge rules');
};

export const useBadgeRulesQuery = (btid: number | null, enabled = true) => {
  const queryFn = useBadgeRulesFn();
  return useQuery({
    queryKey: badgeKeys.rules(btid ?? 0),
    queryFn: () => queryFn(btid!),
    enabled: enabled && typeof btid === 'number' && btid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Stats ────────────────────────────────────────────────────────────────────

const useBadgeStatsFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async () => {
    const raw = await request<unknown>(`${BASE}/stats`);
    return normalizeStats(raw);
  }, 'Failed to fetch badge stats');
};

export const useBadgeStatsQuery = (enabled = true) => {
  const queryFn = useBadgeStatsFn();
  return useQuery({
    queryKey: badgeKeys.stats(),
    queryFn,
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Asset (evidence) ─────────────────────────────────────────────────────────

const useBadgeAssetFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (asid: number) => {
    const raw = await request<unknown>(`/api/v2/asset/${asid}`);
    return normalizeAsset(raw);
  }, 'Failed to fetch asset');
};

export const useBadgeAssetQuery = (asid: number | null, enabled = true) => {
  const queryFn = useBadgeAssetFn();
  return useQuery({
    queryKey: badgeKeys.asset(asid ?? 0),
    queryFn: () => queryFn(asid!),
    enabled: enabled && typeof asid === 'number' && asid > 0,
    staleTime: DEFAULT_STALE_TIME * 12,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Verification checklist ───────────────────────────────────────────────────

const useBadgeVerificationFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (vid: number) => {
    const raw = await request<unknown>(`/api/v2/verification/typed/${vid}`);
    return normalizeVerification(raw);
  }, 'Failed to fetch verification');
};

export const useBadgeVerificationQuery = (vid: number | null, enabled = true) => {
  const queryFn = useBadgeVerificationFn();
  return useQuery({
    queryKey: badgeKeys.verification(vid ?? 0),
    queryFn: () => queryFn(vid!),
    enabled: enabled && typeof vid === 'number' && vid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};
